import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Checkbox,
  Collapse,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { Glossary } from "src/components/Text/Glossary";
import { SearchAndFilterTypes } from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import { GET_VERIFICATIONS_FOR_FILTERS } from "../../graphql/queries";
import { CommonFilterProps } from "../CommonFilters";
import { FilterSettings } from "../FilterSettings";

type Props = CommonFilterProps & {
  formTemplateId: string;
};

interface VerificationFilterSetting {
  status: string;
  verificationIds: string[];
}

const statusList = Object.values(GQL.verification_status_enum);

export const VerificationsFilter = ({
  formTemplateId,
  filterSettings,
}: Props) => {
  const { glossaryTransform } = useGlossary();
  const { remoteData: getVerificationsRemoteData } = useRemoteDataQuery<
    GQL.GetVerificationsForFilters,
    GQL.GetVerificationsForFiltersVariables
  >(GET_VERIFICATIONS_FOR_FILTERS, {
    skip: !formTemplateId,
    variables: {
      form_template_id: formTemplateId,
    },
  });
  const verificationOptions = useMemo(() => {
    if (!getVerificationsRemoteData.hasData()) return [];
    return getVerificationsRemoteData.data.form_verification.map(
      (verification) => ({
        label: verification.label,
        value: verification.id,
        id: verification.id,
      })
    );
  }, [getVerificationsRemoteData]);

  const [verificationFilters, setVerificationFilters] = useState(
    parseVerificationFilters(filterSettings)
  );
  useEffect(() => {
    setVerificationFilters(parseVerificationFilters(filterSettings));
  }, [filterSettings]);

  const handleVerificationFiltersChange = (
    verificationFilterSetting: VerificationFilterSetting
  ) => {
    if (verificationFilterSetting.status !== "All") {
      filterSettings.set(
        SearchAndFilterTypes.VerificationStatus,
        verificationFilterSetting.status
      );
      filterSettings.set(
        SearchAndFilterTypes.VerificationIds,
        ...verificationFilterSetting.verificationIds
      );
    } else {
      filterSettings.clear(SearchAndFilterTypes.VerificationStatus);
      filterSettings.clear(SearchAndFilterTypes.VerificationIds);
    }
    setVerificationFilters(verificationFilterSetting);
  };

  const handleStatusChange = (value: string) => {
    if (value === verificationFilters.status) return;

    handleVerificationFiltersChange({
      status: value,
      verificationIds: [],
    });
  };

  const handleVerificationChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = target;
    const { status, verificationIds } = verificationFilters;

    const exists = verificationIds.includes(value);
    if (checked && !exists) {
      handleVerificationFiltersChange({
        status,
        verificationIds: [...verificationIds, value],
      });
    } else if (!checked && exists) {
      handleVerificationFiltersChange({
        status,
        verificationIds: verificationIds.filter((id) => id !== value),
      });
    }
  };

  const isChecked = (status: string, verificationId: string) => {
    return (
      verificationFilters.status === status &&
      verificationFilters.verificationIds.includes(verificationId)
    );
  };

  if (!verificationOptions.length) return null;

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <AccordionButton paddingX="0">
          <Text variant="filterLabel" flex="1" textAlign="left">
            <Glossary>Verifications</Glossary>
          </Text>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel p="0" mt={4}>
          <RadioGroup
            value={verificationFilters.status}
            onChange={handleStatusChange}
          >
            <VStack align="stretch">
              <Radio value="All">All</Radio>
              {statusList.map((status) => (
                <div key={status}>
                  <Radio value={status}>{glossaryTransform(status)}</Radio>
                  <Collapse in={verificationFilters.status === status}>
                    <VStack align="stretch" paddingX={4}>
                      {verificationOptions.map((verification) => (
                        <Checkbox
                          key={verification.id}
                          value={verification.id}
                          onChange={handleVerificationChange}
                          isChecked={isChecked(status, verification.id)}
                        >
                          {verification.label}
                        </Checkbox>
                      ))}
                    </VStack>
                  </Collapse>
                </div>
              ))}
            </VStack>
          </RadioGroup>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

function parseVerificationFilters(
  filterSettings: FilterSettings<SearchAndFilterTypes>
): VerificationFilterSetting {
  return {
    status:
      filterSettings.get(SearchAndFilterTypes.VerificationStatus)[0] ?? "All",
    verificationIds: filterSettings.get(SearchAndFilterTypes.VerificationIds),
  };
}
